import { FC, MouseEvent } from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";

import { CyberSecurityListItem } from "../../../../models/CybersecurityObject";
import { useActions } from "../../../../hooks/useActions";
import useSearchParams from "../../../../hooks/useSearchParams";
import { useTypedSelector } from "../../../../hooks/useTypedSelector";
import useMobileScreen from "../../../../hooks/useMobileScreen";
import {
  ListViewResultDescription,
  ListViewResultIcon,
  ListViewResultLink,
  ListViewResultMeta,
  ListViewResultRow,
  ListViewResultSection,
  ListViewResultTitle,
  SearchResultItem,
  StyledSearchResultsItemTitle,
} from "../../../../share/searchResults";
import { getWebsiteFavicon } from "../../../../util/faviconUtil";
import {
  getCybersecurityIcon,
  getDomainFromAddress,
} from "../../../../util/cyberSecurityUtil";
import { cyberSecurityServiceCategories } from "../../../../constants/constants";

import styles from "./CybersecuritySearchResultItem.module.css";

type Props = {
  mapObject: CyberSecurityListItem;
};

const CybersecuritySearchResultItem: FC<Props> = ({ mapObject }) => {
  const { setCurrentMarker } = useActions();
  const { setSearchParam } = useSearchParams();
  const isMobile = useMobileScreen();
  const isListViewActive = useTypedSelector(
    (state) => state.mapInterface.isListViewActive
  );
  const shouldRenderListViewLayout = isListViewActive && !isMobile;
  const { t: tServiceCategoryOption } = useTranslation("translation", {
    keyPrefix:
      "mainPage.mapFilters.cybersecurity.serviceCategoryFilter.options",
  });

  const faviconSrc = getWebsiteFavicon(mapObject.website);
  const imgSrc = faviconSrc ?? getCybersecurityIcon(mapObject);

  const websiteRaw = mapObject.website?.trim();
  const websiteHref =
    websiteRaw && websiteRaw.length > 0
      ? websiteRaw.startsWith("http")
        ? websiteRaw
        : `https://${websiteRaw}`
      : undefined;
  const websiteDomain = websiteRaw
    ? getDomainFromAddress(websiteRaw)
    : undefined;

  const serviceCategories = cyberSecurityServiceCategories
    .filter((category) => {
      if (mapObject.categories) {
        return mapObject.categories[
          category as keyof typeof mapObject.categories
        ];
      } else {
        return false;
      }
    })
    .map((category) => tServiceCategoryOption(category))
    .join(", ");

  const handleClick = () => {
    setSearchParam("hash_id", mapObject.edrpou);
  };

  const handleWebsiteClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
  };

  return (
    <SearchResultItem
      hashId={mapObject.edrpou}
      onMouseEnter={() => {
        setCurrentMarker({
          lon: mapObject?.location?.lon ? +mapObject.location.lon : 0,
          lat: mapObject?.location?.lat ? +mapObject.location.lat : 0,
        });
      }}
      onMouseLeave={() => setCurrentMarker({ lon: 0, lat: 0 })}
      onClick={handleClick}
    >
      {shouldRenderListViewLayout ? (
        <NoWrapListViewResultRow>
          <ListViewResultIcon>
            <img
              src={imgSrc}
              alt={mapObject.name}
              className={styles.icon}
              width={36}
              height={36}
            />
          </ListViewResultIcon>
          <NoWrapListViewResultSection flex="2 1 320px">
            <ListViewResultTitle>{mapObject.name}</ListViewResultTitle>
            {serviceCategories && (
              <ListViewResultDescription>
                {serviceCategories}
              </ListViewResultDescription>
            )}
          </NoWrapListViewResultSection>
          <NoWrapListViewResultSection flex="1 1 160px">
            <ListViewResultMeta className={styles.listViewMeta}>
              {mapObject.region || "—"}
            </ListViewResultMeta>
          </NoWrapListViewResultSection>
          <NoWrapListViewResultSection flex="0.8 1 120px">
            <ListViewResultMeta className={styles.listViewMeta}>
              {mapObject.founded_year ?? "—"}
            </ListViewResultMeta>
          </NoWrapListViewResultSection>
          <NoWrapListViewResultSection flex="1 1 160px">
            <ListViewResultMeta className={styles.listViewMeta}>
              {mapObject.company_size || "—"}
            </ListViewResultMeta>
          </NoWrapListViewResultSection>
          <NoWrapListViewResultSection flex="1 1 220px">
            {websiteHref && websiteDomain ? (
              <ListViewResultLink
                href={websiteHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleWebsiteClick}
                className={styles.listViewLink}
              >
                {websiteDomain}
              </ListViewResultLink>
            ) : (
              <ListViewResultMeta>—</ListViewResultMeta>
            )}
          </NoWrapListViewResultSection>
        </NoWrapListViewResultRow>
      ) : (
        <div className={styles.container}>
          <img
            src={imgSrc}
            alt={mapObject.name}
            className={styles.icon}
            width={36}
            height={36}
          />
          <div className={styles.content}>
            <StyledSearchResultsItemTitle className={styles.title}>
              {mapObject.name}
            </StyledSearchResultsItemTitle>
            {serviceCategories && (
              <p className={styles.categories}>{serviceCategories}</p>
            )}
            <p className={styles.meta}>
              <span>{mapObject.edrpou}</span>
              {websiteHref && websiteDomain && (
                <>
                  <span className={styles.metaSeparator}>&middot;</span>
                  <a
                    href={websiteHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.metaLink}
                    onClick={handleWebsiteClick}
                  >
                    {websiteDomain}
                  </a>
                </>
              )}
            </p>
          </div>
        </div>
      )}
    </SearchResultItem>
  );
};

export default CybersecuritySearchResultItem;

const NoWrapListViewResultRow = styled(ListViewResultRow)`
  flex-wrap: nowrap;
`;

const NoWrapListViewResultSection = styled(ListViewResultSection)`
  min-width: 0;
`;
